import { useRef, useEffect, useState } from "react";

//Unform e componentes
import { useField } from "@unform/core";
import IMask from "imask";
import { DayPicker } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  useClick,
  useDismiss,
  useInteractions,
} from "@floating-ui/react";
import SeletorMesAno from "../SeletorMesAno";

//Common
import {
  fn_formatarData,
  fn_getData,
  fn_validarData,
} from "@/common/utils/date";
import { SHOW_DESC_OBRIGATORIO_FIELDS } from "@/common/constantes";

//Util
import { ICONES } from "@/frontend/utils/icones";

import "react-day-picker/style.css";
import "../style/styleCalendario.css";
import { Button } from "reactstrap";

//Intervalo de ano aceito pelas máscaras de data, mês/ano e ano (os pagamentos de contrato longo passam de 2099)
const ANO_MINIMO_MASCARA = 1970;
const ANO_MAXIMO_MASCARA = 2999;

//Período apresentado no calendário: até 2199, para a lista de anos do calendário não ficar enorme
//(data depois de 2199 é digitada no campo)
const CALENDARIO_MES_INICIO = new Date(ANO_MINIMO_MASCARA, 0);
const CALENDARIO_MES_FIM = new Date(2199, 11);

export default function UInputMask({
  name,
  label,
  tipoMask,
  mask,
  defaultValue,
  autoComplete,
  decimalScale = 2,
  decimalFixed = true,
  percentAcceptNegative = false,
  className = "",
  ariaLabel,
  showCalendario = true, //apresenta o botão do calendário quando o tipoMask for "data" ou "mesano" (false esconde)
  ...rest
}) {
  const inputRef = useRef(null);
  const maskRef = useRef(null);

  //Calendário para escolher a data, apresentado somente na máscara de data completa (DD/MM/YYYY),
  //ou o seletor do mês na máscara de mês/ano (MM/YYYY)
  const possuiCalendarioMesAno = tipoMask == "mesano" && showCalendario;
  const possuiCalendario =
    (tipoMask == "data" && showCalendario) || possuiCalendarioMesAno;
  const [openCalendario, setOpenCalendario] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: openCalendario,
    onOpenChange: setOpenCalendario,
    placement: "bottom-end",
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
  ]);

  const { fieldName, registerField, error } = useField(name);
  const defaultAutoComplete = autoComplete || "off";

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,

      getValue: (ref) => {
        return ref.value;
      },

      setValue(ref, value) {
        const safeValue = String(value ?? "");

        if (maskRef.current) {
          maskRef.current.value = safeValue;
          maskRef.current.updateValue();
        } else {
          ref.value = safeValue;
        }
      },

      clearValue(ref) {
        ref.value = "";
      },
    });
  }, [fieldName, registerField]);

  useEffect(() => {
    let maskOptions = { mask };

    //Formatos pré-definidos
    if (tipoMask == "ano") {
      const format = "YYYY";
      maskOptions = {
        mask: Date,
        pattern: "YYYY",

        format: (date) => fn_formatarData(date, format),
        parse: (str) => fn_getData(str, format),

        blocks: {
          YYYY: {
            mask: IMask.MaskedRange,
            from: ANO_MINIMO_MASCARA,
            to: ANO_MAXIMO_MASCARA,
          },
        },
      };
    } else if (tipoMask == "mesano") {
      const format = "MM/YYYY";
      maskOptions = {
        mask: Date,
        pattern: "MM/YYYY",

        format: (date) => fn_formatarData(date, format),
        parse: (str) => fn_getData(str, format),

        blocks: {
          YYYY: {
            mask: IMask.MaskedRange,
            from: ANO_MINIMO_MASCARA,
            to: ANO_MAXIMO_MASCARA,
          },
          MM: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12,
          },
        },
      };
    } else if (tipoMask == "diames") {
      const format = "DD/MM";
      maskOptions = {
        mask: Date,
        pattern: "DD/MM",

        format: (date) => fn_formatarData(date, format),
        parse: (str) => fn_getData(str, format),

        blocks: {
          DD: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 31,
          },
          MM: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12,
          },
        },
      };
    } else if (tipoMask == "data") {
      const format = "DD/MM/YYYY";

      maskOptions = {
        mask: Date,
        pattern: "DD/MM/YYYY",

        format: (date) => fn_formatarData(date, format),
        parse: (str) => fn_getData(str, format),

        blocks: {
          DD: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 31,
          },
          MM: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12,
          },
          YYYY: {
            mask: IMask.MaskedRange,
            from: ANO_MINIMO_MASCARA,
            to: ANO_MAXIMO_MASCARA,
          },
        },
      };
    } else if (tipoMask == "percent") {
      maskOptions = {
        mask: Number,
        scale: decimalScale || 2,
        signed: percentAcceptNegative || false,
        thousandsSeparator: "",
        padFractionalZeros: decimalFixed || true,
        normalizeZeros: true,
        min: percentAcceptNegative ? -100 : 0,
        max: 100,
      };
    } else if (tipoMask == "currency") {
      maskOptions = {
        mask: "R$ num",
        blocks: {
          num: {
            mask: Number,
            thousandsSeparator: ".",
            radix: ",",
            scale: decimalScale || 2,
            mapToRadix: ["."],
          },
        },
      };
    } else if (tipoMask == "telefone") {
      maskOptions = {
        mask: [
          {
            mask: "(00) 0000-0000", // telefone fixo
          },
          {
            mask: "(00) 00000-0000", // celular
          },
        ],
        dispatch: function (appended, dynamicMasked) {
          const number = (dynamicMasked.value + appended).replace(/\D/g, "");

          return number.length > 10
            ? dynamicMasked.compiledMasks[1]
            : dynamicMasked.compiledMasks[0];
        },
      };
    } else if (tipoMask == "cep") {
      maskOptions = {
        mask: "00000-000",
      };
    }

    //const maskField = IMask(inputRef.current, maskOptions);
    //setMaskField(maskField);

    maskRef.current = IMask(inputRef.current, maskOptions);
    maskRef.current.updateValue();

    return () => {
      maskRef.current?.destroy();
    };
  }, [mask, tipoMask]);

  //Retorna a data informada no campo para posicionar o calendário
  function getDataCampo() {
    const valor = inputRef.current?.value;

    return fn_validarData(valor) ? fn_getData(valor).toDate() : undefined;
  }

  //OnSelect - Data escolhida no calendário
  function handleSelectDataCalendario(data) {
    setOpenCalendario(false);

    if (data) {
      //Seta o valor pela máscara para manter o campo e a máscara sincronizados
      maskRef.current.value = fn_formatarData(data, "DD/MM/YYYY");
    }

    //Devolve o foco para o campo, assim a validação da tela (onBlur) ocorre ao sair dele, como na digitação
    inputRef.current.focus();
  }

  //OnSelect - Mês/ano escolhido no seletor do mês
  function handleSelectMesAno(mesAno) {
    setOpenCalendario(false);

    //Seta o valor pela máscara para manter o campo e a máscara sincronizados
    maskRef.current.value = mesAno;

    //Devolve o foco para o campo, como na escolha da data
    inputRef.current.focus();
  }

  const campo = (
    <input
      type="text"
      ref={inputRef}
      id={fieldName}
      name={fieldName}
      aria-label={ariaLabel ? ariaLabel : label}
      defaultValue={defaultValue}
      autoComplete={defaultAutoComplete}
      placeholder={
        tipoMask == "ano"
          ? "YYYY"
          : tipoMask == "mesano"
            ? "MM/YYYY"
            : tipoMask == "diames"
              ? "DD/MM"
              : tipoMask == "data"
                ? "DD/MM/YYYY"
                : ""
      }
      className={("form-control " + className).concat(
        error && SHOW_DESC_OBRIGATORIO_FIELDS == false ? " error" : "",
      )}
      style={
        possuiCalendario
          ? {
              borderRadius: "6px 0px 0px 6px",
            }
          : {}
      }
      {...rest}
    />
  );

  return (
    <div className={label != null ? "form-group" : ""}>
      {label && (
        <label htmlFor={fieldName} className="label">
          {label}
        </label>
      )}

      {(possuiCalendario && (
        <div className="d-flex">
          {campo}

          {/* Ícone do calendário (continuação do campo). O tabIndex -1 mantém a navegação por Tab/Enter somente nos campos */}
          <button
            type="button"
            ref={refs.setReference}
            title={possuiCalendarioMesAno ? "Selecionar mês/ano" : "Selecionar data"}
            className="btn-input-calendario"
            tabIndex={-1}
            disabled={rest.disabled}
            {...getReferenceProps()}
          >
            {ICONES.CALENDARIO}
          </button>

          {openCalendario && (
            <FloatingPortal>
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, zIndex: 9999 }}
                className="calendario-popup"
                {...getFloatingProps()}
              >
                {(possuiCalendarioMesAno && (
                  <SeletorMesAno
                    valor={inputRef.current?.value}
                    anoMinimo={CALENDARIO_MES_INICIO.getFullYear()}
                    anoMaximo={CALENDARIO_MES_FIM.getFullYear()}
                    onSelect={handleSelectMesAno}
                  />
                )) || (
                  <DayPicker
                    mode="single"
                    locale={ptBR}
                    captionLayout="dropdown"
                    startMonth={CALENDARIO_MES_INICIO}
                    endMonth={CALENDARIO_MES_FIM}
                    selected={getDataCampo()}
                    defaultMonth={getDataCampo()}
                    onSelect={handleSelectDataCalendario}
                  />
                )}
              </div>
            </FloatingPortal>
          )}
        </div>
      )) ||
        campo}

      {error && SHOW_DESC_OBRIGATORIO_FIELDS == true && (
        <small className="text-danger">{error}</small>
      )}
    </div>
  );
}
